/**
 * AdminProviderDetailsOverview Component
 * Admin-specific wrapper for ProviderDetailsOverview with inline editing
 * 
 * Wraps key text fields with InlineEditWrapper for quick editing:
 * - professionalTitle (text field)
 * - companyOverview (textarea)
 * - providerBio (textarea)
 * 
 * Complex fields (arrays, relationships) should be edited in Information tab
 */

import { CheckCircle2, Award, Users } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { InlineEditWrapper } from '../inline-edit/InlineEditWrapper';
import { InlineTextField } from '../inline-edit/InlineTextField';
import { InlineTextArea } from '../inline-edit/InlineTextArea';
import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import { hasValue, hasArrayData } from '@/features/provider-directory/utils/fieldValidation';

type AdminProviderDetailsOverviewProps = {
  provider: ProviderProfile;
  serviceRangesText?: string;
  /** Callback to update field values */
  onFieldUpdate: (fieldName: string, value: string) => Promise<void>;
};

/**
 * Admin-specific Overview component with inline editing
 * Focuses on editable text fields; complex fields remain view-only
 */
export function AdminProviderDetailsOverview({
  provider,
  serviceRangesText,
  onFieldUpdate,
}: AdminProviderDetailsOverviewProps) {
  const displayEstablished = provider.establishedYear?.toString() || '';
  const displayServiceRanges = provider.serviceDeliveryTypes || serviceRangesText || '';

  // Check if we have any content for About section
  const hasAboutContent = hasValue(provider.professionalTitle) || 
                          hasValue(provider.companyOverview) || 
                          hasValue(provider.providerBio);

  // Check if we have any content for Key Information
  const hasKeyInfo = hasValue(displayEstablished) ||
                     hasValue(provider.yearsExperience) ||
                     hasArrayData(provider.registeredStates) ||
                     hasArrayData(provider.professionalLicenses) ||
                     hasValue(displayServiceRanges) ||
                     hasArrayData(provider.languages);

  // Check if we have certifications or specialties
  const hasCertOrSpecialties = hasArrayData(provider.certificationsArray) || 
                               hasArrayData(provider.specialties);

  return (
    <div className="space-y-6">
      {/* About Section - Graceful degradation */}
      {hasAboutContent && (
        <Card className="p-6">
          <h2 className="text-xl text-gray-900 mb-4">About {provider.companyName}</h2>
          
          {/* Professional Title - EDITABLE */}
          {hasValue(provider.professionalTitle) && (
            <InlineEditWrapper
              value={provider.professionalTitle || ''}
              onSave={onFieldUpdate}
              fieldName="provider.professionalTitle"
              displayComponent={({ value }) => (
                <p className="text-lg font-semibold text-gray-800 mb-4">{value}</p>
              )}
              editComponent={InlineTextField}
            />
          )}
        
        {hasValue(provider.companyOverview) && (
          <InlineEditWrapper
            value={provider.companyOverview || ''}
            onSave={onFieldUpdate}
            fieldName="provider.companyOverview"
            displayComponent={({ value }) => {
              const paragraphs = value.split('\n\n').filter(p => p.trim());
              return (
                <div className="space-y-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Company Overview</h3>
                  <div className="space-y-4">
                    {paragraphs.map((paragraph, idx) => (
                      <p key={`company-overview-${idx}`} className="text-gray-700">{paragraph}</p>
                    ))}
                  </div>
                </div>
              );
            }}
            editComponent={InlineTextArea}
          />
        )}

        {hasValue(provider.providerBio) && (
          <InlineEditWrapper
            value={provider.providerBio || ''}
            onSave={onFieldUpdate}
            fieldName="provider.providerBio"
            displayComponent={({ value }) => {
              const paragraphs = value.split('\n\n').filter(p => p.trim());
              return (
                <div className="space-y-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Bio</h3>
                  <div className="space-y-4">
                    {paragraphs.map((paragraph, idx) => (
                      <p key={`provider-bio-${idx}`} className="text-gray-700">{paragraph}</p>
                    ))}
                  </div>
                </div>
              );
            }}
            editComponent={InlineTextArea}
          />
        )}
          
          {/* Grid section for certifications and key info - Only show if there's content */}
          {(hasCertOrSpecialties || hasKeyInfo) && (
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Certifications Section - View-only (arrays not editable inline) */}
              {hasCertOrSpecialties && (
                <div>
                  {hasArrayData(provider.certificationsArray) ? (
                    <>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-[#026857]" />
                        Certifications & Credentials
                      </h3>
                      <ul className="space-y-2">
                        {provider.certificationsArray.map((cert, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{cert}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : hasArrayData(provider.specialties) ? (
                    <>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-[#026857]" />
                        Services & Specialties
                      </h3>
                      <ul className="space-y-2">
                        {provider.specialties.map((specialty) => (
                          <li key={specialty.id} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{specialty.name}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              )}

              {/* Key Information Section - View-only (complex fields) */}
              {hasKeyInfo && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#026857]" />
                    Key Information
                  </h3>
                  <div className="space-y-1 text-sm">
                    {hasValue(displayEstablished) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Established:</span>
                        <span className="text-gray-900 text-right">{displayEstablished}</span>
                      </div>
                    )}

                    {hasValue(provider.yearsExperience) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Years of Experience:</span>
                        <span className="text-gray-900 text-right">{provider.yearsExperience}</span>
                      </div>
                    )}

                    {hasArrayData(provider.registeredStates) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">States Licensed In:</span>
                        <span className="text-gray-900 text-right">{provider.registeredStates.join(', ')}</span>
                      </div>
                    )}

                    {hasArrayData(provider.professionalLicenses) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Licenses:</span>
                        <span className="text-gray-900 text-right">{provider.professionalLicenses.join(', ')}</span>
                      </div>
                    )}

                    {hasValue(displayServiceRanges) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Service Delivery:</span>
                        <span className="text-gray-900 text-right">{displayServiceRanges}</span>
                      </div>
                    )}

                    {hasArrayData(provider.languages) && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-600">Languages:</span>
                        <span className="text-gray-900 text-right">{provider.languages.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Care Philosophy Section - EDITABLE (textarea) - Graceful degradation */}
      {hasValue(provider.carePhilosophy) && (
        <Card className="p-6">
          <h2 className="text-xl text-gray-900 mb-4">Care Philosophy</h2>
          <InlineEditWrapper
            value={provider.carePhilosophy || ''}
            onSave={onFieldUpdate}
            fieldName="provider.carePhilosophy"
            displayComponent={({ value }) => (
              <p className="text-gray-700 whitespace-pre-wrap">{value}</p>
            )}
            editComponent={InlineTextArea}
          />
        </Card>
      )}

      {/* Why This Work Section - EDITABLE (textarea) - Graceful degradation */}
      {hasValue(provider.whyThisWork) && (
        <Card className="p-6">
          <h2 className="text-xl text-gray-900 mb-4">Why This Work</h2>
          <InlineEditWrapper
            value={provider.whyThisWork || ''}
            onSave={onFieldUpdate}
            fieldName="provider.whyThisWork"
            displayComponent={({ value }) => (
              <p className="text-gray-700 whitespace-pre-wrap">{value}</p>
            )}
            editComponent={InlineTextArea}
          />
        </Card>
      )}

      {/* Emergency Response Section - View-only (boolean + text) - Graceful degradation */}
      {provider.emergencyResponseAvailable && hasValue(provider.emergencyResponseDetails) && (
        <Card className="p-6">
          <h2 className="text-xl text-gray-900 mb-4">Emergency Response</h2>
          <p className="text-gray-700">{provider.emergencyResponseDetails}</p>
        </Card>
      )}
    </div>
  );
}

